<template>
    <div>
        <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
            {{ $t('Mitgliedsnummern') }}
            <img
                src="@img/info.png"
                alt=""
                :title="$t('Clubity generiert nach dem unten definierten Schema automatisch Mitgliedsnummern für aktivierte Mitglieder.')"
            />
        </h3>

        <div>
            <div class="kgr-inputGroup">
                <div class="kgr-grid kgr-grid--1of2 kgr-grid--gutters">
                    <div class="kgr-grid__cell">
                        <div class="animated fadeIn kgr-grid__cell">
                            <kgr-form-row
                                :label="$t('Prefix')"
                                :error="error"
                                field-name="memberSettings.membershipNumberPrefix"
                            >
                                <input
                                    v-model.trim="memberSettings.membershipNumberPrefix"
                                    type="text"
                                    class="kgr-input"
                                    maxlength="6"
                                    @keyup.enter="onKeyUpEnter"
                                    @change="onUpdatedMemberSettings()"
                                />
                            </kgr-form-row>

                            <kgr-form-row
                                :label="$t('Suffix')"
                                :error="error"
                                field-name="memberSettings.membershipNumberSuffix"
                            >
                                <input
                                    v-model.trim="memberSettings.membershipNumberSuffix"
                                    type="text"
                                    class="kgr-input"
                                    maxlength="6"
                                    @keyup.enter="onKeyUpEnter"
                                    @change="onUpdatedMemberSettings()"
                                />
                            </kgr-form-row>

                            <kgr-form-row
                                :label="$t('Nummernkreis von')"
                                :error="error"
                                field-name="memberSettings.membershipNumberRangeStart"
                                required
                            >
                                <input
                                    v-model.number="memberSettings.membershipNumberRangeStart"
                                    type="number"
                                    min="1"
                                    :max="membershipNumberRangeStartMax"
                                    class="kgr-input"
                                    @keyup.enter="onKeyUpEnter"
                                    @change="onUpdatedMemberSettings()"
                                />
                            </kgr-form-row>

                            <kgr-form-row
                                :error="error"
                                field-name="memberSettings.membershipNumberLeadingZeros"
                            >
                                <kgr-checkbox
                                    v-model="memberSettings.membershipNumberLeadingZeros"
                                    :label="$t('Führende Nullen verwenden')"
                                    class="kgr-row"
                                    @change="onUpdatedMemberSettings()"
                                />
                            </kgr-form-row>

                            <kgr-form-row
                                v-if="memberSettings.membershipNumberLeadingZeros"
                                :label="$t('Stellen des Nummernkreises')"
                                :error="error"
                                field-name="memberSettings.membershipNumberDigits"
                                class="animated fadeIn"
                            >
                                <kgr-generic-select
                                    v-model="memberSettings.membershipNumberDigits"
                                    :options="membershipNumberDigits"
                                    no-null-option
                                    @post-input="onUpdatedMemberSettings()"
                                />
                            </kgr-form-row>

                            <kgr-form-row
                                :error="error"
                                field-name="memberSettings.recycleNumbers"
                            >
                                <kgr-checkbox
                                    v-model="memberSettings.membershipNumberRecycling"
                                    :label="$t('Letzte Nummer des Mitglieds bei zusätzlichem Vertrag wiederverwenden')"
                                    class="kgr-row"
                                />
                            </kgr-form-row>

                            <kgr-form-row
                                :error="error"
                                field-name="memberSettings.recycleNumbers"
                            >
                                <kgr-checkbox
                                    v-model="memberSettings.counterBasedOnPreAndSuffix"
                                    :label="$t('Nummernkreis für jeden Pre- oder Suffix verwenden (nur relevant bei Änderung des Pre- oder Suffixes)')"
                                    class="kgr-row"
                                    @change="onUpdatedMemberSettings()"
                                />
                            </kgr-form-row>
                        </div>
                    </div>

                    <kgr-membership-number-preview
                        v-if="nextMembershipNumber"
                        :number="nextMembershipNumber"
                        :loading="loading"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Checkbox from '../../common/form/checkbox/Checkbox';
import Error from '../../../js/model/Error';
import FormRow from '../../common/form/FormRow';
import GenericSelect from '../../common/form/select/GenericSelect';
import MemberSettings from '../../../js/dto/MemberSettings';
import MembershipNumberPreview from '../../members/membership-number/MembershipNumberPreview';
import MembershipNumberRepository from '../../../js/repository/MembershipNumberRepository';

export default {
    components: {
        'kgr-membership-number-preview': MembershipNumberPreview,
        'kgr-generic-select': GenericSelect,
        'kgr-checkbox': Checkbox,
        'kgr-form-row': FormRow,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        memberSettings: {
            type: MemberSettings,
            required: true,
        },
        error: {
            type: Error,
            required: true,
        },
    },
    data(){
        return {
            loading: false,
            nextMembershipNumber: null,
        }
    },
    computed: {
        membershipNumberDigits() {
            return [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
        },
        membershipNumberRangeStartMax() {
            const digits = this.memberSettings.membershipNumberDigits;
            if (this.membershipNumberDigits.includes(digits)) {
                return Number('9'.repeat(digits));
            }

            return 99999999;
        },
    },
    created() {
        this.loadMembershipNumber();
    },
    methods: {
        onKeyUpEnter() {
            this.$emit('enter');
        },
        loadMembershipNumber() {
            this.loading = true;
            MembershipNumberRepository.getNext(this.clubId, this.memberSettings)
                .then(number => {
                    this.nextMembershipNumber = number;
                    this.loading = false;
                })
                .catch(() => {
                    this.loading = false;
                });
        },
        onUpdatedMemberSettings() {
            if(this.validate()) {
                this.loadMembershipNumber()
            }
        },
        pad(str, max) {
            str = str.toString();
            return str.length < max ? this.pad('0' + str, max) : str;
        },
        validate() {
            let regexPrefix = RegExp(/\d/);
            let regexSuffix = RegExp(/^\d+/);
            const prefixValid = !regexPrefix.test(this.memberSettings.membershipNumberPrefix);
            const suffixValid = !regexSuffix.test(this.memberSettings.membershipNumberSuffix);
            this.error.clear('memberSettings.membershipNumberPrefix')
            this.error.clear('memberSettings.membershipNumberSuffix')
            if (!prefixValid) {
                this.error.add('memberSettings.membershipNumberPrefix', this.$t('Der Prefix darf keine Zahl enthalten.'));
            }
            if (!suffixValid) {
                this.error.add('memberSettings.membershipNumberSuffix', this.$t('Der Suffix darf nicht mit einer Zahl beginnen.'));
            }
            this.$forceUpdate()
            return prefixValid && suffixValid;
        }
    }
}
</script>

<style scoped lang="scss" rel="stylesheet/scss"></style>
